import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { TrendingDown, DollarSign, XCircle } from 'lucide-react';
interface SlideProblemProps {
  onContinue: () => void;
}
export function SlideProblem({
  onContinue
}: SlideProblemProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 600);
    const t2 = setTimeout(() => setStage(2), 1600);
    const t3 = setTimeout(() => setStage(3), 2600);
    const t4 = setTimeout(() => setStage(4), 4000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-523">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-3xl md:text-5xl font-serif text-white mb-12" data-id="element-524">
        Why legacy brands{' '}
        <span className="text-white/50" data-id="element-525">stop getting attention</span>
      </motion.h2>

      <div className="flex-1 flex flex-col justify-center max-w-4xl mx-auto w-full space-y-6" data-id="element-526">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: stage >= 1 ? 1 : 0,
        x: stage >= 1 ? 0 : -20
      }} className="flex items-start gap-5 bg-navy-700/40 border border-navy-600 rounded-2xl p-6" data-id="element-527">
          <div className="w-12 h-12 rounded-xl bg-red-500/15 border border-red-500/30 flex items-center justify-center flex-shrink-0" data-id="element-528">
            <TrendingDown className="w-6 h-6 text-red-400" data-id="element-529" />
          </div>
          <div data-id="element-530">
            <h3 className="text-xl font-semibold text-white mb-2" data-id="element-531">
              Patent expires → Sales drop
            </h3>
            <p className="text-white/60 leading-relaxed" data-id="element-532">
              For small molecules, generic competition causes sales to drop{' '}
              <span className="text-red-400 font-semibold" data-id="element-533">90%+</span> almost
              overnight. Marketing investment is stopped immediately.
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: stage >= 2 ? 1 : 0,
        x: stage >= 2 ? 0 : -20
      }} className="flex items-start gap-5 bg-navy-700/40 border border-navy-600 rounded-2xl p-6" data-id="element-534">
          <div className="w-12 h-12 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0" data-id="element-535">
            <DollarSign className="w-6 h-6 text-amber-400" data-id="element-536" />
          </div>
          <div data-id="element-537">
            <h3 className="text-xl font-semibold text-white mb-2" data-id="element-538">
              Revenue erodes → Resources pulled
            </h3>
            <p className="text-white/60 leading-relaxed" data-id="element-539">
              Sales teams are reassigned. Marketing budgets are cut to bare
              minimum. When the gap between revenue and cost approaches zero,
              the product is{' '}
              <span className="text-red-400 font-medium" data-id="element-540">
                taken off the market
              </span>
              .
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: stage >= 3 ? 1 : 0,
        x: stage >= 3 ? 0 : -20
      }} className="flex items-start gap-5 bg-navy-700/40 border border-red-500/20 rounded-2xl p-6" data-id="element-541">
          <div className="w-12 h-12 rounded-xl bg-red-500/15 border border-red-500/30 flex items-center justify-center flex-shrink-0" data-id="element-542">
            <XCircle className="w-6 h-6 text-red-400" data-id="element-543" />
          </div>
          <div data-id="element-544">
            <h3 className="text-xl font-semibold text-white mb-2" data-id="element-545">
              The real problem: marketing economics
            </h3>
            <p className="text-white/60 leading-relaxed" data-id="element-546">
              It's not that these brands lack value — it's that{' '}
              <span className="text-white font-medium" data-id="element-547">
                traditional marketing is too expensive
              </span>{' '}
              to justify on declining revenue. So brands die earlier than they
              need to.
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: stage >= 4 ? 1 : 0,
        y: stage >= 4 ? 0 : 10
      }} className="bg-navy-800/60 border border-navy-700 rounded-xl px-6 py-4 mt-2" data-id="element-548">
          <p className="text-white/50 leading-relaxed text-sm" data-id="element-549">
            <span className="text-teal-400/80 font-medium" data-id="element-550">
              Note on biologics:
            </span>{' '}
            Biosimilar competition erodes revenue more gradually (15-30% per
            year), so marketing continues longer. But eventually the same
            dynamic applies — declining revenue meets fixed marketing costs, and
            attention fades. Both molecule and biologic brands face the same end
            state.
          </p>
        </motion.div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 4 ? 1 : 0
    }} className="mt-6 flex justify-end" data-id="element-551">
        <Button onClick={onContinue} data-id="element-552">Calculator</Button>
      </motion.div>
    </div>;
}